import * as React from "react"
import { useState, useEffect, useCallback, useMemo } from "react"
import {
    Box, Grid, Card, CardContent, Typography, Button, Chip, TextField, InputAdornment, Tabs, Tab,
    ToggleButton, ToggleButtonGroup, Table, TableHead, TableRow, TableCell, TableBody, IconButton,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
    Checkbox, FormControlLabel
} from "@mui/material"
import { AutocompleteAjaxSeeker, AutocompleteAsyncSeeker, IconSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { getSubmit, postSubmit } from "@ui-seeker/utils"

import { RETAILER_BRANDS_ROUTES, BACKEND_APIS } from "@constants"
import PageWrapper from "Pages/@PageWrapper"
// Reuse the master-data brand creation popup (Drawer controlled by its formData signal)
import AdminBrandsForm from "../Brands/Brand/Drawer"
import { formData as brandFormData } from "../Brands/Brand/@signals"
import generateHexColor from "../Brands/Brand/@utils"
import CompetitorBrandsPanel from "./CompetitorBrandsPanel"

const RB = BACKEND_APIS.RETAILER_BRANDS
const idOf = (v) => (v && typeof v === 'object' ? v.value : v)

export default function RetailerBrandsPage() {
    const { showToast } = useAppToast()

    const [customer, setCustomer] = useState(null)
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [running, setRunning] = useState(false)        // populate/normalize in flight
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState('all')
    const [endDate, setEndDate] = useState('')   // only count products seen since (optional)
    const [firstDate, setFirstDate] = useState('') // only count products NEW since (first_date, optional)
    const [comp, setComp] = useState({})         // id_brand -> lazy competitor counts
    const [tab, setTab] = useState(0)            // 0 = retailer brands, 1 = competitor brands
    const [assignTarget, setAssignTarget] = useState(null)   // {brand} being assigned
    const [assignBrand, setAssignBrand] = useState(null)     // selected canonical brand
    const [savingAssign, setSavingAssign] = useState(false)
    const [customerNames, setCustomerNames] = useState({})   // {id: name} for batch naming
    const [batchTarget, setBatchTarget] = useState(null)     // row for which a batch is being created
    const [batchName, setBatchName] = useState('')
    const [creatingBatch, setCreatingBatch] = useState(false)
    const [batchAutoSecondPass, setBatchAutoSecondPass] = useState(true)  // AI 2nd pass ON by default
    const [batchRetryNotFound, setBatchRetryNotFound] = useState(false)
    const [batchSpeedMode, setBatchSpeedMode] = useState(false)           // OFF = OpenAI Batch API (cheap, slow)
    const [selectedForBatch, setSelectedForBatch] = useState({})   // brand -> row (multi-select, normalized only)

    const cid = idOf(customer)

    // Retailer customer names (for the auto batch name "Customer - Brand")
    useEffect(() => {
        getSubmit(`${BACKEND_APIS.BATCH_ADMIN}/customers?clientType=retailer`)
            .then(res => {
                const m = {}
                ;(res || []).forEach(o => { m[o.value] = (o.label || '').replace(/\s*-\s*\d+$/, '') })
                setCustomerNames(m)
            })
            .catch(() => {})
    }, [])

    const openCreateBatch = (row) => {
        const cname = customerNames[cid] || `Customer ${cid}`
        const bname = row.canonical_name || row.brand
        setBatchTarget(row)
        setBatchName(`${cname} - ${bname}`)
        setBatchAutoSecondPass(true)
        setBatchRetryNotFound(false)
        setBatchSpeedMode(false)
    }

    const openCreateBatchBulk = () => {
        setBatchTarget({ bulk: true, rows: Object.values(selectedForBatch) })
        setBatchName('')
        setBatchAutoSecondPass(true)
        setBatchRetryNotFound(false)
        setBatchSpeedMode(false)
    }

    const _batchConfig = () => {
        const cfg = { retryNotFound: batchRetryNotFound, maxDaysSinceSeen: 90 }
        if (batchAutoSecondPass) {
            // Second pass transport: 'batch' (OpenAI Batch API) by default — ~50%
            // cheaper, but decisions only land when OpenAI resolves the batch (SLA
            // up to 24h), so the batch sits in QA unvalidated until then. Speed mode
            // falls back to 'sync' (per-row, applies in minutes) for an urgent brand.
            // The shadow pair that measured batch-vs-sync is no longer launched from
            // here; it stays available via the Batch Admin second-pass dialog.
            cfg.autoSecondPass = { model: 'gpt-5.6-luna', threshold: 0.8, nf_threshold: 0.9,
                                   sample_rate: 0, verify_images: true,
                                   execution_mode: batchSpeedMode ? 'sync' : 'batch' }
        }
        return cfg
    }

    const doCreateBatch = async () => {
        if (!cid) return
        const cname = customerNames[cid] || `Customer ${cid}`
        // Bulk: one batch per selected brand, shared config, auto-named per brand.
        const targets = batchTarget?.bulk
            ? batchTarget.rows
            : (batchTarget?.id_brand ? [{ ...batchTarget, _name: batchName.trim() }] : [])
        if (!targets.length) return
        setCreatingBatch(true)
        try {
            let ok = 0, fail = 0
            for (const t of targets) {
                const name = t._name || `${cname} - ${t.canonical_name || t.brand}`
                try {
                    await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches`, {
                        batchType: 'raw2raw', batchName: name, customerID: cid,
                        brands: [t.id_brand], splitBySites: false, ..._batchConfig(),
                    })
                    ok += 1
                } catch (e) { fail += 1 }
            }
            showToast[fail ? 'warning' : 'success'](
                `${ok} batch${ok === 1 ? '' : 'es'} created${fail ? ` · ${fail} failed` : ''}`)
            setBatchTarget(null); setSelectedForBatch({})
        } catch (e) {
            showToast.error('Error creating batch')
        } finally {
            setCreatingBatch(false)
        }
    }

    const load = useCallback(() => {
        if (!cid) { setRows([]); return }
        setLoading(true)
        const params = new URLSearchParams({ customerID: cid })
        if (search.trim()) params.set('search', search.trim())
        if (status !== 'all') params.set('status', status)
        if (endDate) params.set('endDate', endDate)
        if (firstDate) params.set('firstDate', firstDate)
        getSubmit(`${RB}?${params.toString()}`)
            .then(res => setRows(res?.brands || []))
            .catch(() => { showToast.error('Could not load retailer brands'); setRows([]) })
            .finally(() => setLoading(false))
    }, [cid, search, status, endDate, firstDate])

    useEffect(() => {
        const t = setTimeout(load, 300)
        return () => clearTimeout(t)
    }, [load])

    // Competitor counts load ON DEMAND only (per-brand eye button, or "Load
    // all"): even the chunked lazy version was too slow on big customers —
    // now nothing hits the aggregation endpoint until the user asks.
    const [compLoading, setCompLoading] = useState({})   // id_brand -> true while fetching
    const [loadingAll, setLoadingAll] = useState(false)

    useEffect(() => { setComp({}); setCompLoading({}) }, [cid, endDate, firstDate])

    const loadComp = useCallback(async (ids) => {
        if (!cid || !ids.length) return
        setCompLoading(prev => ({ ...prev, ...Object.fromEntries(ids.map(i => [i, true])) }))
        const params = new URLSearchParams({ customerID: cid, brands: ids.join(',') })
        if (endDate) params.set('endDate', endDate)
        if (firstDate) params.set('firstDate', firstDate)
        try {
            const res = await getSubmit(`${RB}/competitor-counts?${params.toString()}`)
            if (res?.counts) setComp(prev => ({ ...prev, ...res.counts }))
        } catch (e) {
            showToast.error('Could not load competitor counts')
        } finally {
            setCompLoading(prev => {
                const next = { ...prev }
                ids.forEach(i => delete next[i])
                return next
            })
        }
    }, [cid, endDate, firstDate])

    const loadAllComp = useCallback(async () => {
        const ids = [...new Set(rows.map(r => r.id_brand).filter(Boolean))]
            .filter(i => !comp[i] && !compLoading[i])
        setLoadingAll(true)
        try {
            for (let i = 0; i < ids.length; i += 25) {
                await loadComp(ids.slice(i, i + 25))
            }
        } finally {
            setLoadingAll(false)
        }
    }, [rows, comp, compLoading, loadComp])

    const counts = useMemo(() => ({
        total: rows.length,
        normalized: rows.filter(r => r.normalized).length,
        pending: rows.filter(r => !r.normalized).length,
        products: rows.reduce((s, r) => s + (r.product_count || 0), 0),
    }), [rows])

    const runAction = async (path, body, label) => {
        if (!cid) return
        setRunning(true)
        try {
            const res = await postSubmit(`${RB}/${path}`, { id_customer: cid, ...body })
            const summary = res?.matched_exact != null
                ? `exact ${res.matched_exact} · fuzzy ${res.matched_fuzzy} · created ${res.created} · unmatched ${res.unmatched}`
                : (res?.inserted != null ? `${res.inserted} inserted / ${res.updated || 0} updated` : 'done')
            showToast.success(`${label}: ${summary}`)
            load()
        } catch (e) {
            showToast.error(`Error running ${label}`)
        } finally {
            setRunning(false)
        }
    }

    const doAssign = async (brand, id_brand) => {
        setSavingAssign(true)
        try {
            await postSubmit(`${RB}/assign`, { id_customer: cid, brand, id_brand })
            showToast.success(id_brand ? 'Brand normalized' : 'Normalization cleared')
            setAssignTarget(null); setAssignBrand(null)
            load()
        } catch (e) {
            showToast.error('Error assigning brand')
        } finally {
            setSavingAssign(false)
        }
    }

    return (
        <PageWrapper
            head={RETAILER_BRANDS_ROUTES.TITLE}
            title={RETAILER_BRANDS_ROUTES.TITLE}
            subtitle={RETAILER_BRANDS_ROUTES.SUBTITLE}
            actions={null}
        >
            <Grid container spacing={2}>
                {/* Toolbar */}
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 280 }}>
                                <AutocompleteAjaxSeeker
                                    urlAjax={`${BACKEND_APIS.BATCH_ADMIN}/customers`}
                                    filters={{ clientType: 'retailer' }}
                                    label="Customer"
                                    placeholder="Select a customer…"
                                    value={customer}
                                    onChange={e => setCustomer(e.target.value)}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            <Button variant="outlined"
                                    startIcon={<IconSeeker icon="square-plus" />}
                                    onClick={() => { brandFormData.value = { brandID: null, color: generateHexColor(), customerID: null } }}>
                                New Brand
                            </Button>
                            <Button variant="outlined" disabled={!cid || running}
                                    startIcon={<IconSeeker icon="arrows-rotate" />}
                                    onClick={() => runAction('populate', {}, 'Populate')}>
                                Populate
                            </Button>
                            <Button variant="contained" disabled={!cid || running}
                                    startIcon={running ? <CircularProgress size={16} /> : <IconSeeker icon="wand-magic-sparkles" />}
                                    onClick={() => runAction('normalize', { fuzzy_threshold: 93 }, 'Normalize')}>
                                Normalize
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                {cid && (
                    <Grid item xs={12}>
                        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
                            <Tab label="Retailer brands" />
                            <Tab label="Competitor brands" />
                        </Tabs>
                    </Grid>
                )}
                {cid && tab === 1 && (
                    <Grid item xs={12}>
                        <CompetitorBrandsPanel cid={cid} />
                    </Grid>
                )}
                {!cid ? (
                    <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                            <IconSeeker icon="tags" sx={{ fontSize: 48, mb: 1 }} />
                            <Typography>Select a retailer to review and normalize its brands.</Typography>
                        </Box>
                    </Grid>
                ) : tab === 1 ? null : (
                    <>
                        {/* Filters + counts */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                <TextField size="small" placeholder="Search brand…" value={search}
                                           onChange={e => setSearch(e.target.value)} sx={{ minWidth: 240 }}
                                           InputProps={{ startAdornment: (
                                               <InputAdornment position="start"><IconSeeker icon="search" sx={{ color: '#9ca3af' }} /></InputAdornment>
                                           ) }} />
                                <ToggleButtonGroup size="small" exclusive value={status}
                                                   onChange={(e, v) => v && setStatus(v)}>
                                    <ToggleButton value="all">All</ToggleButton>
                                    <ToggleButton value="pending">Pending</ToggleButton>
                                    <ToggleButton value="normalized">Normalized</ToggleButton>
                                </ToggleButtonGroup>
                                <TextField size="small" type="date" label="Seen since (end date)"
                                           value={endDate} onChange={e => setEndDate(e.target.value)}
                                           InputLabelProps={{ shrink: true }} sx={{ minWidth: 185 }}
                                           inputProps={{ title: 'Only count products (own and competitor) whose SKU_MAPPING end_date is on/after this date' }} />
                                <TextField size="small" type="date" label="New since (first date)"
                                           value={firstDate} onChange={e => setFirstDate(e.target.value)}
                                           InputLabelProps={{ shrink: true }} sx={{ minWidth: 185 }}
                                           inputProps={{ title: 'Only count products (own and competitor) whose SKU_MAPPING first_date is on/after this date (new since)' }} />
                                <Box sx={{ flex: 1 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {counts.total} brands · {counts.normalized} normalized · {counts.pending} pending
                                </Typography>
                                <Button size="small" variant="outlined" disabled={loadingAll || !rows.length}
                                        startIcon={loadingAll ? <CircularProgress size={14} /> : <IconSeeker icon="eye" />}
                                        onClick={loadAllComp}>
                                    {loadingAll
                                        ? `Competitors ${Object.keys(comp).length}/${[...new Set(rows.map(r => r.id_brand).filter(Boolean))].length}`
                                        : 'Load all competitors'}
                                </Button>
                                {Object.keys(selectedForBatch).length > 0 && (
                                    <Button size="small" variant="contained" color="success"
                                            startIcon={<IconSeeker icon="layer-plus" />}
                                            onClick={openCreateBatchBulk}>
                                        Create {Object.keys(selectedForBatch).length} batches
                                    </Button>
                                )}
                            </Box>
                        </Grid>

                        {/* Table */}
                        <Grid item xs={12}>
                            <Card variant="outlined">
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
                                ) : rows.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No brands found.</Box>
                                ) : (
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    {(() => {
                                                        const norm = rows.filter(r => r.normalized && r.id_brand)
                                                        const all = norm.length > 0 && norm.every(r => selectedForBatch[r.brand])
                                                        const some = norm.some(r => selectedForBatch[r.brand])
                                                        return (
                                                            <Checkbox size="small" checked={all}
                                                                      indeterminate={some && !all} disabled={norm.length === 0}
                                                                      onChange={() => setSelectedForBatch(
                                                                          all ? {} : Object.fromEntries(norm.map(r => [r.brand, r])))} />
                                                        )
                                                    })()}
                                                </TableCell>
                                                <TableCell>Retailer brand</TableCell>
                                                <TableCell align="right">Products</TableCell>
                                                <TableCell align="right">Sites</TableCell>
                                                <TableCell>Canonical brand</TableCell>
                                                <TableCell align="right">Competitor products</TableCell>
                                                <TableCell align="right">Pending map</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map(r => (
                                                <TableRow key={r.brand} hover selected={!!selectedForBatch[r.brand]}>
                                                    <TableCell padding="checkbox">
                                                        {r.normalized && r.id_brand && (
                                                            <Checkbox size="small" checked={!!selectedForBatch[r.brand]}
                                                                      onChange={() => setSelectedForBatch(prev => {
                                                                          const next = { ...prev }
                                                                          if (next[r.brand]) delete next[r.brand]; else next[r.brand] = r
                                                                          return next
                                                                      })} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 500 }}>{r.brand}</TableCell>
                                                    <TableCell align="right">{r.product_count.toLocaleString()}</TableCell>
                                                    <TableCell align="right">{r.sites}</TableCell>
                                                    <TableCell>
                                                        {r.normalized ? (
                                                            <Chip size="small" color={r.inconsistent ? 'warning' : 'success'} variant="outlined"
                                                                  label={r.inconsistent ? `${r.canonical_name || r.id_brand} (mixed)` : (r.canonical_name || r.id_brand)} />
                                                        ) : (
                                                            <Chip size="small" variant="outlined" label="— pending" sx={{ color: 'text.disabled' }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {!r.normalized ? (
                                                            <Typography variant="body2" color="text.disabled">—</Typography>
                                                        ) : (() => {
                                                            const c = comp[r.id_brand]
                                                            if (!c) {
                                                                if (compLoading[r.id_brand]) return <CircularProgress size={14} />
                                                                return (
                                                                    <Tooltip title="Load competitor counts for this brand">
                                                                        <IconButton size="small" onClick={() => loadComp([r.id_brand])}>
                                                                            <IconSeeker icon="eye" sx={{ fontSize: 15 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )
                                                            }
                                                            return c.breakdown && c.breakdown.length > 0 ? (
                                                                <Tooltip arrow title={
                                                                    <Box>
                                                                        {c.breakdown.map(b => (
                                                                            <Box key={b.site} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                                <span>{b.site}</span><span>{b.count.toLocaleString()}</span>
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                }>
                                                                    <Typography variant="body2" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                                                                        {c.total.toLocaleString()}
                                                                    </Typography>
                                                                </Tooltip>
                                                            ) : (
                                                                <Typography variant="body2">0</Typography>
                                                            )
                                                        })()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {!r.normalized ? (
                                                            <Typography variant="body2" color="text.disabled">—</Typography>
                                                        ) : (() => {
                                                            const c = comp[r.id_brand]
                                                            if (!c) {
                                                                return compLoading[r.id_brand]
                                                                    ? <CircularProgress size={14} />
                                                                    : <Typography variant="body2" color="text.disabled">—</Typography>
                                                            }
                                                            return (
                                                                <Tooltip arrow title={
                                                                    c.breakdown && c.breakdown.length > 0 ? (
                                                                        <Box>
                                                                            {c.breakdown.map(b => (
                                                                                <Box key={b.site} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                                    <span>{b.site}{b.attempted === false ? ' — no batch yet' : ''}</span>
                                                                                    <span>{(b.unmapped || 0).toLocaleString()}</span>
                                                                                </Box>
                                                                            ))}
                                                                        </Box>
                                                                    ) : 'Competitor products not yet in any match group'
                                                                }>
                                                                    <Box sx={{ cursor: 'help', display: 'inline-block' }}>
                                                                        <Typography variant="body2" sx={{
                                                                            fontWeight: (c.unmapped || 0) > 0 ? 600 : 400,
                                                                            color: (c.unmapped || 0) > 0 ? 'warning.main' : 'text.secondary'
                                                                        }}>
                                                                            {(c.unmapped || 0).toLocaleString()}
                                                                        </Typography>
                                                                        {(c.noBatch || 0) > 0 && (
                                                                            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', lineHeight: 1 }}>
                                                                                {c.noBatch.toLocaleString()} no batch
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Tooltip>
                                                            )
                                                        })()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {r.normalized && (
                                                            <Tooltip arrow title="Create raw2raw batch for this brand (all competitor sites)">
                                                                <Button size="small" color="success" startIcon={<IconSeeker icon="layer-plus" />}
                                                                        onClick={() => openCreateBatch(r)}>
                                                                    Batch
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                        <Button size="small" onClick={() => { setAssignTarget({ brand: r.brand }); setAssignBrand(null) }}>
                                                            {r.normalized ? 'Reassign' : 'Assign'}
                                                        </Button>
                                                        {r.normalized && (
                                                            <Button size="small" color="error" onClick={() => doAssign(r.brand, null)}>
                                                                Clear
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Card>
                        </Grid>
                    </>
                )}
            </Grid>

            {/* Manual assign dialog */}
            <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>Normalize brand</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Retailer brand: <strong>{assignTarget?.brand}</strong>
                    </Typography>
                    <AutocompleteAsyncSeeker
                        urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
                        label="Canonical brand (DIPI_BRAND_NEW)"
                        placeholder="Search canonical brand…"
                        value={assignBrand}
                        onChange={e => setAssignBrand(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignTarget(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!idOf(assignBrand) || savingAssign}
                            startIcon={savingAssign ? <CircularProgress size={16} /> : null}
                            onClick={() => doAssign(assignTarget.brand, idOf(assignBrand))}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Quick create raw2raw batch dialog */}
            <Dialog open={!!batchTarget} onClose={() => setBatchTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>{batchTarget?.bulk ? `Create ${batchTarget.rows.length} raw2raw batches` : 'Create raw2raw batch'}</DialogTitle>
                <DialogContent>
                    {batchTarget?.bulk ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            One batch per selected brand (<strong>{batchTarget.rows.length}</strong>), all competitor
                            sites, auto-named "Customer - Brand". Config below applies to all.
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Brand <strong>{batchTarget?.canonical_name || batchTarget?.brand}</strong> · all competitor sites of this retailer · defaults for the rest.
                                {batchTarget?.competitor_products != null && (
                                    <> · ~{(batchTarget.competitor_products || 0).toLocaleString()} competitor products.</>
                                )}
                            </Typography>
                            <TextField fullWidth size="small" label="Batch name"
                                       value={batchName} onChange={e => setBatchName(e.target.value)} />
                        </>
                    )}
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel control={
                            <Checkbox size="small" checked={batchAutoSecondPass}
                                      onChange={e => setBatchAutoSecondPass(e.target.checked)} />
                        } label={
                            <Box>
                                <Typography variant="body2">AI second pass when the batch finishes</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    gpt-5.6-luna · conf 0.8 · not-found 0.9 · QA sample 0% · photos on
                                </Typography>
                            </Box>
                        } />
                        {/* Transport for the second pass above: cheap OpenAI-Batch queue by
                            default, immediate per-row run when the brand is urgent. */}
                        <FormControlLabel sx={{ ml: 3 }} control={
                            <Checkbox size="small" checked={batchSpeedMode} disabled={!batchAutoSecondPass}
                                      onChange={e => setBatchSpeedMode(e.target.checked)} />
                        } label={
                            <Box>
                                <Typography variant="body2" color={batchAutoSecondPass ? 'text.primary' : 'text.disabled'}>
                                    Speed mode — run the second pass immediately
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {batchSpeedMode
                                        ? 'Results in minutes · full price'
                                        : 'Off: OpenAI Batch queue · ~50% cheaper · up to 24h until the batch is validated'}
                                </Typography>
                            </Box>
                        } />
                        <FormControlLabel control={
                            <Checkbox size="small" checked={batchRetryNotFound}
                                      onChange={e => setBatchRetryNotFound(e.target.checked)} />
                        } label={
                            <Typography variant="body2">Retry not-found products (only those with a new unmapped candidate)</Typography>
                        } />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBatchTarget(null)}>Cancel</Button>
                    <Button variant="contained" color="success"
                            disabled={creatingBatch || (!batchTarget?.bulk && !batchName.trim())}
                            startIcon={creatingBatch ? <CircularProgress size={16} /> : <IconSeeker icon="layer-plus" />}
                            onClick={doCreateBatch}>
                        {batchTarget?.bulk ? `Create ${batchTarget.rows.length} batches` : 'Create batch'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Master-data brand creation popup (same as Brands dashboard) */}
            <AdminBrandsForm />
        </PageWrapper>
    )
}
